import { clockDimensions } from "@next-platform/packages-ts-types";
import { createDefaultDayLevels, createDefaultTodLevels } from "./rulebookDraftFactory";
/** Seed default levels for any enabled dimension that has none, so the clock stays valid. */
const withSeededLevels = (meter, dimensions) => ({
    ...meter,
    rule_dimensions: dimensions,
    tod_levels: dimensions.includes("tod") && meter.tod_levels.length === 0 ? createDefaultTodLevels() : meter.tod_levels,
    day_levels: dimensions.includes("day") && meter.day_levels.length === 0 ? createDefaultDayLevels() : meter.day_levels,
});
/**
 * Turn one rule dimension of a story clock on or off. At least one dimension stays on, date-only clocks
 * can't use time of day, and a dimension turned on without levels gets the default levels.
 */
export function toggleClockDimension({ meter, dimension }) {
    const enabled = clockDimensions(meter);
    if (enabled.includes(dimension)) {
        const next = enabled.filter((entry) => entry !== dimension);
        return next.length === 0 ? meter : { ...meter, rule_dimensions: next };
    }
    if (dimension === "tod" && meter.mode === "date") {
        return meter;
    }
    return withSeededLevels(meter, [...enabled, dimension]);
}
/** Step a clock gets when advancing is switched on for a mode that had none (minutes, or messages per day). */
export const DEFAULT_CLOCK_STEP = 10;
/** The step field "Advance with each message" drives in each mode. */
export const clockStepField = (mode) => mode === "date" ? "step_every_messages" : "step_minutes";
/** Keep "Advance with each message" on or off across a mode switch: each mode has its own step field. */
const carryAdvance = (meter, mode) => {
    const isAdvancing = meter[clockStepField(meter.mode)] > 0;
    const field = clockStepField(mode);
    if (isAdvancing === meter[field] > 0) {
        return meter;
    }
    return { ...meter, [field]: isAdvancing ? DEFAULT_CLOCK_STEP : 0 };
};
/**
 * Switch between date & time and date-only. Date-only drops the time-of-day dimension and falls back to days.
 * Advancing stays on or off as it was.
 */
export function setClockMode({ meter, mode }) {
    const carried = carryAdvance(meter, mode);
    if (mode !== "date") {
        return { ...carried, mode };
    }
    const withoutTod = clockDimensions(carried).filter((dimension) => dimension !== "tod");
    return { ...withSeededLevels(carried, withoutTod.length ? withoutTod : ["day"]), mode };
}
